import type { FC } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
//   studio.discountModal.form.audienceInput.option.*
//   studio.discountRow.offLabel*
//   studio.discountRow.offLabel
//   studio.discountRow.offLabel.firstMonthOnly
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import CopyIcon from '@wix/da-ds/pkg/Icons/24x24/Copy';
import { PapiSellerDiscount } from '@wix/da-papi-types';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiData,
  DuplicateDiscountClickBiEvent,
  EndDiscountClickBiEvent,
  EditDiscountClickBiEvent,
} from '@wix/da-bi/pkg/events';
import {
  DATE_FORMATS,
  formatDate,
  formatTime,
  TIME_FORMATS,
} from '@wix/da-i18next/pkg/time/functions';
import Flex from '@wix/da-ds/pkg/Flex';
import SellerDiscountModal from '../SellerDiscountModal';
import EndDiscountButton from './EndDiscountButton';
import { useSelector } from 'react-redux';
import { getActiveSection } from '../../../../selectors/studio';
import DeleteDiscountButton from './DeleteDiscountButton';
import {
  useButtonVisibility,
  useDeleteDiscount,
  useDiscountRowActions,
  useEndDiscount,
} from '../SellerDiscountModal/hooks';
import MoreButton from './MoreButton';
import { isDiscountActive, isDiscountEnded } from '../helpers';
import { getAppliesToBabelKeyNew } from '../SellerDiscountModal/_partials/DiscountPreviewCard/helpers';
import { StudioSection } from '../../../../types/store';

import s from './DiscountRow.scss';

interface Props {
  discount: PapiSellerDiscount;
  className?: string;
  statusSection?: StudioSection;
}

const DiscountRow: FC<Props> = ({ discount, className, statusSection }) => {
  const { t, i18n } = useTranslation();
  const logBiEvent = useBiLogger();
  const activeSection = useSelector(getActiveSection);
  const sectionToUse = statusSection ?? activeSection;
  const isActive = isDiscountActive(discount);
  const isEnded = isDiscountEnded(discount);

  const { showDelete, showDuplicate, showEdit, showEndDiscount } =
    useButtonVisibility(sectionToUse, isActive);
  const { deleteDiscount } = useDeleteDiscount();
  const handleDelete = () => {
    deleteDiscount(discount.discountId);
  };
  const { endDiscount } = useEndDiscount();
  const handleEndDiscount = () => {
    logBiEvent(
      BiData<EndDiscountClickBiEvent>({
        evid: 453,
        campaignid: discount.discountId,
      })
    );
    endDiscount(discount.discountId);
  };
  const {
    isModalOpen,
    modalMode,
    openEditModal,
    openDuplicateModal,
    closeModal,
  } = useDiscountRowActions(discount.discountId);
  const handleDuplicateDiscount = () => {
    logBiEvent(
      BiData<DuplicateDiscountClickBiEvent>({
        evid: 456,
        campaignid: discount.discountId,
      })
    );
    openDuplicateModal();
  };

  const prettyStartDate = formatDate(
    discount.startDate,
    DATE_FORMATS.Date.Short,
    i18n.language
  );
  const prettyStartTime = formatTime(
    discount.startDate,
    TIME_FORMATS.HourMinute,
    i18n.language
  )
    .replace(/:00/, '')
    .toUpperCase();

  const prettyEndDate = formatDate(
    discount.endDate,
    DATE_FORMATS.Date.Short,
    i18n.language
  );
  const prettyEndTime = formatTime(
    discount.endDate,
    TIME_FORMATS.HourMinute,
    i18n.language
  )
    .replace(/:00/, '')
    .toUpperCase();

  const isFirstMonthOnly = discount.appliesto === 'any_tier';

  // discount.appliesto from API is the final value (e.g., 'any_tier', 'any_shop_product')
  const appliesToString = t(getAppliesToBabelKeyNew(discount.appliesto));

  const audienceString = t(
    `studio.discountModal.form.audienceInput.option.${discount.audience}`
  );

  return (
    <div className={c(s['root'], className)}>
      <div className={c(s['col'], s['col-name'])}>
        <div className={s['discount-name']}>{discount.name}</div>
        <div className={s['col-subheader']}>
          {t(
            `studio.discountRow.discountOff${isFirstMonthOnly ? '.firstMonthOnly' : ''}`,
            {
              percent: discount.amount?.percent,
              productType: appliesToString,
            }
          )}
        </div>
      </div>
      <Flex className={s['col-group-1']} direction="row" gap={32}>
        <Flex className={s['col-date-group']} direction="row" gap={16}>
          <div className={c(s['col'], s['col-date'])}>
            <div className={s['col-header']}>{prettyStartDate}</div>
            <div className={s['col-subheader']}>
              {t(
                isEnded
                  ? 'studio.discountRow.startedAt'
                  : 'studio.discountRow.startsAt',
                { time: prettyStartTime }
              )}
            </div>
          </div>
          <div className={c(s['col'], s['col-date'])}>
            <div className={s['col-header']}>{prettyEndDate}</div>
            <div className={s['col-subheader']}>
              {t(
                isEnded
                  ? 'studio.discountRow.endedAt'
                  : 'studio.discountRow.endsAt',
                { time: prettyEndTime }
              )}
            </div>
          </div>
        </Flex>
      </Flex>
      <div className={s['col-audience']}>{audienceString}</div>
      <div className={s['col-buttons']}>
        <div className={s['expanded-buttons']}>
          {showEdit && (
            <IconButton
              size="medium"
              variant="main"
              icon={EditIcon}
              onClick={openEditModal}
              aria-label={t('common.edit')}
              title={t('common.edit')}
              biData={BiData<EditDiscountClickBiEvent>({
                evid: 452,
                campaignid: discount.discountId,
              })}
            />
          )}
          {showDuplicate && (
            <IconButton
              size="medium"
              variant="main"
              icon={CopyIcon}
              onClick={openDuplicateModal}
              biData={BiData<DuplicateDiscountClickBiEvent>({
                evid: 456,
                campaignid: discount.discountId,
              })}
              aria-label={t('common.duplicate')}
              title={t('common.duplicate')}
            />
          )}
          {showDelete && <DeleteDiscountButton discount={discount} />}
          {showEndDiscount && <EndDiscountButton discount={discount} />}
        </div>
        <MoreButton
          discount={discount}
          isActive={isActive}
          className={s['more-button']}
          activeSection={sectionToUse}
          onEditClick={openEditModal}
          onDuplicateClick={handleDuplicateDiscount}
          onDeleteClick={handleDelete}
          onEndDiscountClick={handleEndDiscount}
        />
        {isModalOpen && (
          <SellerDiscountModal
            discount={discount}
            mode={modalMode}
            onRequestClose={closeModal}
          />
        )}
      </div>
    </div>
  );
};

export default DiscountRow;
